/**
 * The one definition of the hot / warm / cool bands over `contacts.engagement_score`.
 *
 * WHY THIS EXISTS
 * The score is computed on a 0–100 scale (`calculateEngagementScore` in
 * server/routes/contacts.ts caps at 100). Four places read it and three of them
 * disagreed, verified 2026-08-27:
 *
 *   ContactCard / ContactDetailModal      >= 70 Hot, >= 50 Warm
 *   AppShell (global search results)      >= 80 Hot, >= 50 Warm
 *   storage.getDashboardEngagementMetrics >= 700 Hot, >= 300 Warm
 *   storage.getDashboardNewLeadsMetrics   >= 700 Hot, >= 300 Warm
 *
 * The last two are thresholds for a 0–1000 scale — the scale the column's own
 * comment still claims — applied to a score that can never exceed 100. So the
 * "Lead Temperature" widget and the engagement donut counted EVERY contact as
 * Cool, for every project, however engaged: the Hot and Warm segments were
 * structurally unreachable, not merely empty. AppShell put the same contact one
 * band lower than the card the user had just clicked through from.
 *
 * 70/50 is kept because it is what the two contact surfaces have always shown,
 * so no contact changes band on a screen anyone has been reading.
 */

/** Score at or above which a contact reads as Hot. */
export const ENGAGEMENT_HOT = 70;

/** Score at or above which a contact reads as Warm. */
export const ENGAGEMENT_WARM = 50;

/** Highest score `calculateEngagementScore` will produce. */
export const ENGAGEMENT_MAX = 100;

/**
 * The score every contact is born with — Hot, deliberately.
 *
 * A lead in this business raises its hand once, and the moment it does is the
 * moment the team should be looking at it. The old rule started everyone at 0
 * and made them earn their way up a points table, so a registration submitted
 * this morning read Cool and the band told a salesperson the opposite of the
 * truth. Every contact now ENTERS Hot and cools from there.
 *
 * Imported contacts are the exception: they arrived when they registered, not
 * when the file was uploaded — see `arrivalOf` in server/lib/engagementScore.ts.
 *
 * It is the score {@link ENGAGEMENT_ARRIVAL_WEIGHT} produces on day zero
 * (100 × 1.0), and the two must agree: the storage layer writes this at insert
 * time, and a row that changed the first night the recompute saw it would be
 * the rule contradicting itself.
 */
export const ENGAGEMENT_ENTRY_SCORE = 100;

/**
 * How fast heat fades: every signal is worth half as much after this many days.
 *
 * 30 days is chosen so the bands read off a calendar. An untouched lead is 100
 * the day it arrives, Hot for about two weeks (0.5^(15/30) ≈ 0.71), Warm until
 * the end of the first month (exactly 50 at day 30) and Cool after that. A
 * visit or a meeting puts that same clock back near the top; an email we sent
 * barely moves it.
 *
 * Exponential rather than the old one-point-a-day line because a line needs a
 * reset rule, and the reset rule was the bug: ANY interaction put the clock
 * back to zero, so one synced email — including one the salesperson sent —
 * made a contact brand new again. Decay applied to each signal separately has
 * no reset; an old visit and a new email each carry exactly what they are
 * worth today.
 */
export const ENGAGEMENT_HALF_LIFE_DAYS = 30;

/** The contact arriving is itself a signal, at full strength. */
export const ENGAGEMENT_ARRIVAL_WEIGHT = 1;

/**
 * What each kind of interaction says about the BUYER, as a heat in [0, 1] on
 * the day it happened.
 *
 * Ordered by what it costs the buyer to do it. Walking into a sales centre is
 * an afternoon; answering an email is a minute; an email WE send costs them
 * nothing at all — it is the salesperson's effort, not the buyer's interest,
 * and on its own it must never be able to keep a lead Hot. That last point is
 * not hypothetical: the mailbox sync writes one touchpoint per message, and
 * 5,606 of production's 10,424 synced emails (2026-09-28) are outbound.
 */
export const ENGAGEMENT_WEIGHTS = {
  /** Filled in the registration form (again). The buyer raised their hand. */
  registration: 1.0,
  /** Sales-centre visit, model viewing, showing, meeting — in person. */
  inPerson: 0.9,
  /** Open house or launch event — in person, but in a crowd. */
  event: 0.7,
  /** A phone call, either direction: a live conversation, but not in the room. */
  call: 0.5,
  /**
   * Showroom on the buyer's own phone, after scanning a QR code at a sales
   * centre screen: browsing plans and lots, saving, emailing a plan. Andrew,
   * 2026-10-21: activity on the sales centre screens "should be weighted more
   * heavily than the QR code scan interactions". The screens are the buyer in
   * the room (inPerson); the phone is the buyer on their own, often later and
   * at home — more than writing to us, well short of being there. A hold or
   * offer request made on the phone is the exception: the strongest buying
   * signal Showroom has, it weighs as inPerson (engagementScore.ts SHOWROOM).
   */
  showroomPhone: 0.4,
  /** The buyer wrote to us — an inbound email, SMS or portal chat. */
  inboundVirtual: 0.35,
  /**
   * The buyer clicked a link in one of our marketing emails (ActiveCampaign,
   * campaign or automation). Andrew, 2026-10-20: email interactions "should
   * also factor into the lead scoring". A deliberate act, just below writing
   * to us; a click on another day is another signal.
   */
  emailClick: 0.3,
  /**
   * A visit to the project's website by a known lead (Website Analytics,
   * Phase 2): the buyer looking on their own, as deliberate as a click on
   * one of our emails. One a day counts, however many visits.
   */
  websiteVisit: 0.3,
  /**
   * The buyer opened one. Light, and only the most recent counts (see
   * engagementScore.ts): Apple Mail opens every message on arrival, so a run
   * of opens may be a phone, not a person.
   */
  emailOpen: 0.12,
  /** A completed follow-up or deposit-reminder task. */
  followUp: 0.25,
  /** We wrote to them — an outbound (or undirected) email or SMS. Effort, not engagement. */
  outboundVirtual: 0.1,
  /** Somebody wrote a note down. */
  note: 0.1,
  /** A type nobody has weighted yet. Still something that happened. */
  unknown: 0.1,
} as const;

export type EngagementBand = "hot" | "warm" | "cool";

/**
 * The band a score falls in, or `null` when there is no score at all.
 *
 * `null` and `0` are different answers: a contact with no score has not been
 * measured, and the surfaces that show a badge render nothing for it rather
 * than claiming it is Cool.
 */
export function getEngagementBand(score: number | null | undefined): EngagementBand | null {
  if (score === undefined || score === null) return null;
  if (score >= ENGAGEMENT_HOT) return "hot";
  if (score >= ENGAGEMENT_WARM) return "warm";
  return "cool";
}

/**
 * Title-case label for a band, as every surface spells it.
 *
 * Typed with `satisfies` rather than annotated `Record<EngagementBand, string>`:
 * the annotation widens the values to `string`, and the engagement editor in
 * ContactDetailModal holds the label in state typed as the three literals.
 */
export const ENGAGEMENT_BAND_LABEL = {
  hot: "Hot",
  warm: "Warm",
  cool: "Cool",
} as const satisfies Record<EngagementBand, string>;

export type EngagementBandLabel = (typeof ENGAGEMENT_BAND_LABEL)[EngagementBand];

/**
 * The Lead Score filter's options, and the band each one selects.
 *
 * These are matched by their LABEL — the panel hands the chosen strings back as
 * plain text — so the numbers in them are not decoration: a label saying
 * "Hot (≥80)" against a `getEngagementBand` that turns Hot at 70 is a filter
 * that silently drops every contact between the two. Both sides read
 * shared/engagement.ts, so the label cannot drift from the predicate again.
 */
export const LEAD_SCORE_FILTER_LABEL = {
  hot: `${ENGAGEMENT_BAND_LABEL.hot} (≥${ENGAGEMENT_HOT})`,
  warm: `${ENGAGEMENT_BAND_LABEL.warm} (${ENGAGEMENT_WARM}-${ENGAGEMENT_HOT - 1})`,
  cool: `${ENGAGEMENT_BAND_LABEL.cool} (<${ENGAGEMENT_WARM})`,
} as const satisfies Record<EngagementBand, string>;

export const LEAD_SCORE_FILTER_OPTIONS = [
  LEAD_SCORE_FILTER_LABEL.hot,
  LEAD_SCORE_FILTER_LABEL.warm,
  LEAD_SCORE_FILTER_LABEL.cool,
];
